import React, { ComponentType, lazy } from 'react';

/**
 * Enhanced lazy import with automatic retry on chunk load failure.
 * When Vite builds a new version, old hashed chunks (e.g. LeadModal-_Gyp1PyL.js)
 * may no longer exist on the server. If a client still has the old HTML cached
 * or the network hiccups, this utility automatically recovers by reloading once
 * or retrying the import.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const pageHasBeenForceRefreshed = window.sessionStorage.getItem('chunk_reload_retry');

    try {
      const component = await componentImport();
      window.sessionStorage.removeItem('chunk_reload_retry');
      return component;
    } catch (error: any) {
      const isChunkLoadFailed =
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Importing a module script failed') ||
        error?.message?.includes('error loading dynamically imported module');

      if (isChunkLoadFailed && !pageHasBeenForceRefreshed) {
        // Mark that we are doing an automatic reload so we don't get into an infinite loop
        window.sessionStorage.setItem('chunk_reload_retry', 'true');
        window.location.reload();
        // Return a pending promise so the error boundary is not triggered before the page reloads
        return new Promise<{ default: T }>(() => {});
      }

      // If already reloaded or different error, rethrow
      throw error;
    }
  });
}
